import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { getDateFromString } from 'utils/date';

import useTrls from 'hooks/translation/useTrls';

import { AuctionBuilderDatesErrorCode, BiddingStrategy } from 'store/models/auctionData';
import {
    setMultiCreationEndAtValidationError,
    setMultiCreationStartAtValidationError,
} from 'store/models/multiCreation';
import { useSelector } from 'store/utils';

const TrlKeys = {
    error: {
        [AuctionBuilderDatesErrorCode.ValueMissingForStrategy]:
            'AuctionBuilder.CampaignPeriod.error.valueMissingForStrategy',
        [AuctionBuilderDatesErrorCode.EndDateBiggerThanStartDate]:
            'AuctionBuilder.CampaignPeriod.error.endDateBiggerThanStartDate',
    },
};

type UseMultiCreationDateValidation = {
    validateStartDate: (date: string | null) => void;
    validateEndDate: (date: string | null) => void;
};

export const useMultiCreationDateValidation = (vacancyId: number): UseMultiCreationDateValidation => {
    const currentAuctionsToCreate = useSelector(({ multiCreation }) => multiCreation.auctionsToCreate[vacancyId]);
    const { startAt } = currentAuctionsToCreate;
    const biddingStrategy = useSelector(({ multiCreation }) => multiCreation.biddingStrategy);

    const trls = useTrls();
    const dispatch = useDispatch();

    const validateStartDate = useCallback(
        (value: string | null) => {
            let error: string | null = null;

            if (biddingStrategy === BiddingStrategy.Auto && !value) {
                error = trls(TrlKeys.error[AuctionBuilderDatesErrorCode.ValueMissingForStrategy]);
            }

            dispatch(
                setMultiCreationStartAtValidationError({
                    vacancyId,
                    error,
                })
            );
        },
        [biddingStrategy, dispatch, trls, vacancyId]
    );

    const validateEndDate = useCallback(
        (value: string | null) => {
            let error: string | null = null;
            const valueAsDate = getDateFromString(value);
            const startAtAsDate = getDateFromString(startAt);

            if (biddingStrategy === BiddingStrategy.Auto && !value) {
                error = trls(TrlKeys.error[AuctionBuilderDatesErrorCode.ValueMissingForStrategy]);
            }

            if (valueAsDate && startAtAsDate && valueAsDate < startAtAsDate) {
                error = trls(TrlKeys.error[AuctionBuilderDatesErrorCode.EndDateBiggerThanStartDate]);
            }

            dispatch(
                setMultiCreationEndAtValidationError({
                    vacancyId,
                    error,
                })
            );
        },
        [biddingStrategy, dispatch, startAt, trls, vacancyId]
    );

    return { validateStartDate, validateEndDate };
};
